import { useMemo } from "react"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import type { EditableTestCase } from "../model"
import { getPendingEditForTestCase } from "../utils/pendingChangesValidation"

/**
 * Hook to get the pending edit for a specific field of a test case.
 *
 * Usage:
 *   const { hasPendingEdit, oldValue, newValue } = usePendingEdit(testCase, "description");
 *
 * For array fields (expected_behaviors), use usePendingEditArray instead.
 */
export function usePendingEdit<K extends keyof EditableTestCase>(
  testCase: EditableTestCase,
  field: K,
) {
  const { pendingChanges } = useTestSetEditorContext()

  return useMemo(() => {
    const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)
    const hasPendingEdit = pendingEdit?.[field] !== undefined

    return {
      hasPendingEdit,
      pendingEdit,
      oldValue: (testCase[field] ?? "") as string,
      newValue: hasPendingEdit
        ? ((pendingEdit[field] ?? "") as string)
        : undefined,
    }
  }, [testCase, field, pendingChanges])
}
